import React, { useMemo } from 'react';
import useDataSource from 'hooks/useDataSource';
import { percent, round, sum } from 'shared/constants/math';
import { dayjs } from 'shared/utils/date';
import DashboardCard from 'components/DashboardCard';
import { MODULE_LABEL } from 'shared/constants/settings';
import { FormattedRelativeTime } from 'react-intl';
import selectUnit from 'shared/utils/selectUnit';
import { MODULE_PATH } from 'shared/constants/survey';
import { EDIT_LICENSE, EDIT_SURVEY } from 'shared/constants/permissions';
import { Module } from 'shared/prisma/types';
import { hasModuleLicense } from 'shared/utils/license';
import CenterContent from 'components/CenterContent';
import { Link } from 'react-router-dom';
import { Button } from 'antd';

const extraFields = `
  surveyGroups(statsDateRanges:[{ predefined: LAST_30_DAYS }]) {
    id
    title
    surveys {
      id
      title
      viewCounts
      responseCounts
      lastResponse
    }
  }
`;

const mapSurvey = (survey) => {
  const { viewCounts, responseCounts, lastResponse } = survey;
  const [viewCountRecent] = viewCounts;
  const [responseCountRecent] = responseCounts;

  return {
    ...survey,
    viewCountRecent,
    responseCountRecent,
    lastResponse: lastResponse && dayjs(lastResponse),
  };
};

const mapSurveyGroup = (group) => {
  const surveys = group.surveys.map(s => mapSurvey(s));

  return {
    viewCountRecent: sum(surveys, { valueSelector: s => s.viewCountRecent }) || null,
    responseCountRecent: sum(surveys, { valueSelector: s => s.responseCountRecent }) || null,
    lastResponse: surveys.reduce((d, s) => (!d || s.lastResponse?.isAfter(d) ? s.lastResponse : d), null),
    surveys,
  };
};

function getSurveyStatistics(groups) {
  const views = sum(groups, { valueSelector: v => v.viewCountRecent });
  const responses = sum(groups, { valueSelector: v => v.responseCountRecent });
  const lastResponse = groups.reduce((d, s) => (!d || s.lastResponse?.isAfter(d) ? s.lastResponse : d), null);

  return [
    { title: 'Active surveys', value: groups.filter(s => s.viewCountRecent > 0).length },
    { title: 'Views', value: views },
    { title: 'Responses', value: responses },
    { title: 'Response rate', value: round(percent(responses, views), 1) || 0, suffix: '%' },
    { title: 'Last response', value: lastResponse?.toDate(), formatter: time => (time ? <FormattedRelativeTime {...selectUnit(time)} /> : '-') },
  ];
}

const SurveysCard = ({ module }) => {
  const { dataSource, dataSourceId, loading, hasDataSourcePermission } = useDataSource({
    extraFields,
  });

  const hasSurveys = hasModuleLicense(dataSource?.activeLicense, Module.UxSurveys);
  const statistics = useMemo(() => getSurveyStatistics(dataSource?.surveyGroups.map(group => mapSurveyGroup(group)) || []), [dataSource]);
  const canEdit = hasDataSourcePermission(EDIT_SURVEY);
  let cardContent = null;
  let cardProps = {};

  if (hasSurveys) {
    cardProps = {
      statistics,
      resultsUrl: `/data-sources/${dataSourceId}/${MODULE_PATH[module]}/dashboard`,
      manageUrl: canEdit && `/data-sources/${dataSourceId}/${MODULE_PATH[module]}/list`,
    };
  } else {
    const canEditLicense = hasDataSourcePermission(EDIT_LICENSE);
    cardContent = (
      <CenterContent>
        <p>
          Your license does not include surveys.
        </p>
        {canEditLicense && (
          <Link to={`/data-sources/${dataSourceId}/settings/license`}>
            <Button type="primary">
              Upgrade license
            </Button>
          </Link>
        )}
      </CenterContent>
    );
  }

  return (
    <DashboardCard
      loading={loading}
      module={module}
      title={MODULE_LABEL[module]}
      period="Last 30 days"
      manageLabel="Manage surveys"
      disabled={!hasSurveys}
      {...cardProps}
    >
      {cardContent}
    </DashboardCard>
  );
};

export default SurveysCard;
